import React, { useState } from 'react';
import { 
  TextField, 
  Button, 
  Typography, 
  Grid, 
  CircularProgress,
  Paper,
  Box
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  generateAIContent,
  getPersonDetails,
  createAIResponse 
} from '../services/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AIPromptPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiResponse, setAiResponse] = useState('');
  const [prompt, setPrompt] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Get person details first
      const personDetails = await getPersonDetails();
      
      // Combine with user's prompt
      const fullPrompt = `${prompt}, Person Details: ${personDetails}`;
      
      // Generate AI content
      const aiResult = await generateAIContent(fullPrompt);
      const generatedText = aiResult.candidates[0].content.parts[0].text;
      setAiResponse(generatedText);
      
      // Save to AI API
      await createAIResponse({
        prompt: fullPrompt,
        response: generatedText,
        requester: 1 // Assuming requester ID is 1
      });

      toast.success('AI response generated!');
      
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        AI Prompt
      </Typography>
      
      <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
        {/* Prompt Input */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Enter your prompt"
            multiline
            rows={4}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            required
          />
        </Box>
        
        {/* AI Response */}
        {aiResponse && (
          <Box sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Response:
            </Typography>
            <Paper elevation={2} sx={{ 
              p: 3, 
              backgroundColor: '#f5f5f5',
              maxHeight: '500px',
              overflow: 'auto',
              '& h3': {
                fontSize: '1.2rem',
                fontWeight: 600,
                color: 'primary.main',
                mt: 3,
                mb: 2
              },
              '& ul': {
                pl: 2,
                mb: 2
              },
              '& li': {
                mb: 1
              },
              '& strong': {
                color: 'primary.dark'
              }
            }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {aiResponse}
              </ReactMarkdown>
            </Paper>
          </Box>
        )}
        
        {/* Submit Button */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          mt: 3
        }}>
          <Button 
            color="primary" 
            variant="contained" 
            type="submit"
            disabled={isSubmitting}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {isSubmitting ? <CircularProgress size={24} /> : aiResponse ? 'Regenerate' : 'Generate'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default AIPromptPage;